import "./UpdatePanel.css"
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { ISubentity, isLinkedSubentity } from "../../../entities/Subentities";
import { ItemCreation, IHeader, ICommand } from '../ItemCreation';
import { Validator, IValidator } from "../../../validation";
import * as Metadata from '../../../entities/Metadata';
import * as UsersStore from '../../../store/User';
import { DisplayFieldService } from '../../common/DisplayFieldService';
import { OnChangeEvent } from "../sectionsControl/uiControls/fieldsArea/FieldComponent";
import { filterUnique, notUndefined } from '../../utils/common';
import { Dictionary, EntityType } from '../../../entities/common';
import { css } from 'office-ui-fabric-react';
import FieldsEdit from './FieldsEdit';
import ExtraTabs, { IExtraTab } from '../ExtraTabs/ExtraTabs';
import HeaderText from './HeaderText';
import { FieldConfig } from './FieldEditor';
import { SecondaryMessage } from '../ExtraTabs/HeaderSecondaryText';
import { ViewService } from '../../../services/ViewService';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ExtensionInfo, ExtensionPageLocation, findExtension } from '../../../store/ExtensionStore';
import ExtensionPanel from '../ExtensionPanel';

const EmptyTabArray: IExtraTab[] = [];

// AssignedTo is the one field carrying compound data: membership + per-assignee Units.
type Assignee = { id: string, units?: number | null };

// True when two assignee lists have the SAME set of resource ids (Units/order ignored) — i.e. no add/remove. Used to
// keep an AssignedTo supersede-clear from dropping a real membership add (only a units-only override is superseded).
const sameAssigneeMembership = (a?: Assignee[], b?: Assignee[]): boolean => {
    const bIds = new Set((b ?? []).map(_ => _.id));
    const aList = a ?? [];
    return aList.length === bIds.size && aList.every(_ => bIds.has(_.id));
};

// Restore each already-known assignee's AUTHORITATIVE Units on a membership add/remove. The pickers build their new
// list out of the DISPLAYED one, and displayed Units can be DERIVED (on a Flexible task Units are a function of
// Effort/Duration/span), so an add/remove would bake a derived value into `changes` as if the user had typed it —
// making the next render read "same members, different Units" as an in-place Units edit and re-derive the span from it
// (removing the assignee just added to a 5d/60h/150% task produced 10d/75% instead of restoring 5d/150%). The
// authoritative value is the one last EMITTED for that assignee, i.e. what `startedFrom` holds: the previous `changes`
// entry, or the stored entity when the user has not touched assignees yet. A real Units edit is left untouched.
const keepAuthoritativeUnits = (emitted: Assignee[], startedFrom: Assignee[] | undefined): Assignee[] => {
    const previous = new Map((startedFrom ?? []).map(_ => [_.id, _.units]));
    return emitted.map(a => previous.has(a.id) && previous.get(a.id) !== a.units
        ? { ...a, units: previous.get(a.id) }
        : a);
};

// The order in which the user made their edits, for a `deriveDisplayAttributes` that needs to know which edit is the
// LATEST. `changes` alone cannot say: it accumulates, and on the CREATE panel it accumulates EVERY value the user
// entered (there is no stored entity to diff against), so a Duration typed after an assignment is indistinguishable
// from one typed before it. Supersede-on-edit removes only the edits a later one makes stale, not the ones that stay
// valid alongside it.
export type PanelEdits = {
    // Edited field names, least-recently-edited first; each field appears once, at the position of its LAST edit.
    order: string[];
    // The most recent AssignedTo edit changed per-assignee Units only, i.e. the membership it started from (the panel's
    // previous state, which on create is the previously entered assignees rather than the stored ones) is unchanged.
    unitsOnly?: boolean;
};

export type GenericPanelProps<TSubentity extends ISubentity> = React.PropsWithChildren<{
    extraTabs?: IExtraTab[];
    subentityType: EntityType;
    subentityTypeLabel: ((entity: TSubentity) => string) | string;
    operation?: string;
    className?: ((entity: TSubentity) => string) | string;

    readOnly?: boolean;
    header?: Partial<IHeader>;
    secondaryMessage?: SecondaryMessage;

    onDismiss: () => void;

    fields: ((entity: TSubentity) => Metadata.Field[]) | Metadata.Field[];
    displayFields: string[];
    mandatoryEditFields?: string[];
    uiControlElementsCustomRender?: Dictionary<any>;
    customFieldValidatorBuilder?: Dictionary<(state: TSubentity, field: Metadata.Field) => IValidator>;
    // Optional pure DISPLAY derivation: given the original entity and the user's raw edits, returns the derived
    // attribute overlay to SHOW (e.g. task scheduling — Duration/Due/Units the server will compute). Recomputed fresh
    // each render, never persisted (save sends `changes` only). See task Fields.buildDeriveDisplayAttributes.
    deriveDisplayAttributes?: (entity: TSubentity, changes: Dictionary<unknown>, edits?: PanelEdits) => Dictionary<unknown>;
    // Optional: turn (entity, changes, derived, edits) into the payload to SAVE. Defaults to `changes` — the user's raw
    // edits, which the server re-derives from. A caller overrides it when the payload alone cannot express what
    // the user did: the server sees no edit ORDER, so "set Duration then assign" and "assign then set Duration"
    // reach it as the same keys and must not (see task Fields.buildTaskSavePayload). `edits` is the same edit history
    // `deriveDisplayAttributes` gets, because the same question ("which edit was last?") decides both.
    buildSavePayload?: (entity: TSubentity, changes: Dictionary<unknown>, derived: Dictionary<unknown>, edits?: PanelEdits) => Dictionary<unknown>;
    editableNativeFieldsForLinkedEntities?: ((entity: TSubentity) => string[]) | string[];
    theOnlyEditableFieldsForLinkedEntities?: string[];
    readOnlyFields?: string[] | ((entity: TSubentity) => string[]);
    mandatoryEditableFields?: (entity: TSubentity) => string[] | undefined;
    isDirty?: boolean;
    customCommands?: ICommand[];
    parentEntityId?: string;
    parentEntityType?: EntityType;
}>

// `derived` is the DISPLAY overlay the panel was showing (deriveDisplayAttributes' output) — passed on so a caller that
// persists the whole entity (the create panel) saves exactly what the user saw instead of re-deriving it.
export type OnComplete = (id: string, changes: Dictionary<unknown>, derived?: Dictionary<unknown>) => void;

type OwnProps<TSubentity extends ISubentity> = GenericPanelProps<TSubentity> & {
    onComplete: OnComplete;
    onDelete?: (entityId: string) => void;
    entity: TSubentity;
    onRefresh?: () => void;
};

interface StateProps {
    panelExtension?: ExtensionInfo;
    user: UsersStore.UserState;
}

interface ActionProps {
    userActions: typeof UsersStore.actionCreators,
}

type Props<TSubentity extends ISubentity> = OwnProps<TSubentity> & StateProps & ActionProps;

const UpdatePanelComponent = <TSubentity extends ISubentity>(props: Props<TSubentity>) => {
    const { subentityType, entity, onDismiss, onComplete, onDelete, onRefresh, extraTabs = EmptyTabArray, children, operation,
        uiControlElementsCustomRender, customFieldValidatorBuilder, editableNativeFieldsForLinkedEntities, user,
        theOnlyEditableFieldsForLinkedEntities, readOnlyFields, customCommands, panelExtension, parentEntityId, parentEntityType,
        deriveDisplayAttributes, buildSavePayload } = props;

    // If extension exists, render ExtensionPanel instead of the form
    if (panelExtension) {
        return <ExtensionPanel
            entityType={subentityType}
            info={panelExtension}
            context={{ 
                entityId: entity.id, 
                entityType: subentityType,
                sourceType: entity.sourceType,
                parentEntityId: parentEntityId,
                parentEntityType: parentEntityType,
            }}
            onRefresh={onRefresh}
            onDismiss={onDismiss}
        />;
    }

    const mandatoryEditableFields = React.useMemo(() => props.mandatoryEditableFields?.(entity) ?? [], [entity, props.mandatoryEditableFields]);
    const readOnly = !!props.readOnly && !mandatoryEditableFields.length;

    const [selectedTab, setSelectedTab] = React.useState<IExtraTab | undefined>();
    const onTabChange = React.useCallback(
        (key: string) => setSelectedTab(extraTabs.find(_ => _.key === key)),
        [extraTabs]);

    // `changes` and the edit order are ONE state: the order is derived from the previous changes (an AssignedTo edit is
    // units-only relative to whatever was there before it), so they must be updated together in a single reducer.
    const [{ changes, edits }, setEditState] = React.useState<{ changes: Dictionary<unknown>, edits: PanelEdits }>(
        { changes: {}, edits: { order: [] } });

    // full entity necessary for:
    // predecessor field type to filter entity from suggestion list
    // to correctly render entity changes in extraTabs because they can use some entity specific data
    //
    // Display = original attributes + the user's real edits (`changes`) + a fresh DERIVED overlay computed from
    // (entity + changes + the edit order). The overlay (task scheduling: Duration/Due/Units/Effort the server will
    // produce) is recomputed from scratch every render — never accumulated — so it cannot drift, and it is
    // DISPLAY-ONLY: `onComplete` persists `changes` (plus, for a create, this same overlay). The overlay wins over
    // `changes` for its (derived, mutually consistent) scheduling keys; non-derived edits (Name, Status, Progress, …)
    // come straight from `changes`.
    const derived = React.useMemo(
        () => deriveDisplayAttributes?.(entity, changes, edits) ?? {},
        [changes, edits, entity, deriveDisplayAttributes]);
    const editEntity = React.useMemo(() => ({
        ...entity,
        attributes: { ...entity.attributes, ...changes, ...derived }
    }), [changes, derived, entity]);

    const isFieldReadOnly = (field: Metadata.Field): boolean => {
        return props.readOnly || field.isReadonly 
            || ViewService.isReadonlyField(field, readOnlyFields, subentityType, entity, editableNativeFieldsForLinkedEntities, theOnlyEditableFieldsForLinkedEntities);
    }
    
    const fields = props.fields instanceof Function ? props.fields(editEntity) : props.fields;
    const config: Dictionary<FieldConfig> = React.useMemo(
        () => fields
            .map(_ => ({
                fieldName: _.name,
                renderInput: uiControlElementsCustomRender?.[_.name],
                validator: !mandatoryEditableFields.includes(_.name) && isFieldReadOnly(_) ? undefined :
                    customFieldValidatorBuilder?.[_.name]?.(editEntity as TSubentity, _) || DisplayFieldService.buildValidator(_),
                readOnly: !mandatoryEditableFields.includes(_.name) && isFieldReadOnly(_)
            }))
            .reduce((p, c) => ({ ...p, [c.fieldName]: c }), {}),
        [fields, editEntity, mandatoryEditableFields]);

    const displayFields: string[] = React.useMemo(
        () => filterUnique((props.mandatoryEditFields || []).concat(props.displayFields)),
        [props.displayFields, props.mandatoryEditFields]);

    const isValid = React.useMemo(
        () => {
            const validators = fields
                .map(_ => _.name)
                .filter(_ => config[_]?.validator)
                .reduce((v, k) => ({ ...v, [k]: config[k].validator }), {});
            return Validator.isValid(validators, editEntity.attributes);
        },
        [config, editEntity, fields]);

    const subentityTypeLabel = props.subentityTypeLabel instanceof Function ? props.subentityTypeLabel(entity) : props.subentityTypeLabel;
    const className = props.className instanceof Function ? props.className(entity) : props.className;

    const [isDirty, setIsDirty] = React.useState(props.isDirty && isValid);
    const setDirty = React.useCallback(() => setIsDirty(true), []);
    const onNameChanged = React.useCallback((newName: string) => {
        setEditState((p) => ({ ...p, changes: { ...p.changes, "Name": newName } }));
        setDirty();
    }, []);
    const onChanged = React.useCallback<OnChangeEvent>(
        (f, v, extra) => {
            // `changes` holds only the user's real edits (the edited field + any intrinsic consequences such as the
            // Progress/Work recalc). Derived scheduling values are NOT stored here — they are recomputed for display
            // by deriveDisplayAttributes and derived by the server on save from this minimal payload.
            // "Last change wins": an `extra` key with an `undefined` value REMOVES that key from `changes`, any other
            // value SETS it — a field handler uses this to supersede a now-stale dependent edit (e.g. editing Duration
            // clears a prior DueDate edit so Due re-derives from the new Duration, not the other way round). AssignedTo
            // is the one field carrying compound data (membership + per-assignee Units), so an AssignedTo clear drops
            // it ONLY when it is a units-only override (same membership as the stored entity) — a real add/remove is
            // never dropped by a superseding date/duration/effort edit.
            setEditState((p) => {
                const next: Dictionary<unknown> = { ...p.changes, [f]: v };
                if (extra) {
                    Object.keys(extra).forEach((k) => {
                        if (extra[k] !== undefined) {
                            next[k] = extra[k];
                        } else if (k === "AssignedTo") {
                            if (sameAssigneeMembership(next["AssignedTo"] as Assignee[] | undefined, entity.attributes["AssignedTo"] as Assignee[] | undefined)) {
                                delete next["AssignedTo"];
                            }
                        } else {
                            delete next[k];
                        }
                    });
                }
                // Record this field as the most recent edit, and (for AssignedTo) whether it kept the membership it
                // started from — the assignees ALREADY entered/stored, which on the create panel live in `p.changes`.
                const startedFrom = (p.changes["AssignedTo"] ?? entity.attributes["AssignedTo"]) as Assignee[] | undefined;
                const unitsOnly = f === "AssignedTo"
                    && !!startedFrom?.length
                    && sameAssigneeMembership(v as Assignee[] | undefined, startedFrom);
                if (f === "AssignedTo" && !unitsOnly && Array.isArray(next["AssignedTo"])) {
                    next["AssignedTo"] = keepAuthoritativeUnits(next["AssignedTo"] as Assignee[], startedFrom);
                }
                return {
                    changes: next,
                    edits: {
                        order: [...p.edits.order.filter(_ => _ !== f), f],
                        unitsOnly: f === "AssignedTo" ? unitsOnly : p.edits.unitsOnly
                    }
                };
            });
            setDirty();
        },
        [entity]);

    const commands = React.useMemo(() => {
        return [
            !readOnly ? {
                primary: true,
                text: `${operation ? operation : "Save"} ${subentityTypeLabel}`,
                onClick: () => { onComplete(entity.id, buildSavePayload?.(entity, changes, derived, edits) ?? changes, derived); onDismiss(); },
                disabled: !isValid || !isDirty
            } : undefined,
            {
                text: readOnly ? 'Close' : 'Cancel',
                onClick: onDismiss
            },
            !props.readOnly && onDelete
                ? getDeleteButtonCommand(() => onDelete(entity.id))
                : undefined,
        ]
            .filter(notUndefined)
            .concat(customCommands || []);
            
    }, [entity, entity.id, changes, edits, derived, onDismiss, onComplete, onDelete, readOnly, subentityTypeLabel, isValid, isDirty, customCommands, buildSavePayload]);

    const saveFullScreenPanelState = React.useCallback((expanded: boolean) => {
        if (!props.header?.showFullScreenMode) {
            return;
        }
        props.userActions.updateEditPanelPreferences(subentityType, expanded);
    }, [props.header?.showFullScreenMode, subentityType]);

    return <ItemCreation onDismiss={props.onDismiss}
        header={{
            nameEditorLabel: `${subentityTypeLabel} Name`,
            nameEditorDescription: Metadata.getNameField(fields)?.description,
            disableNameEditor: readOnly || isLinkedSubentity(subentityType, entity),
            onChanged: onNameChanged,
            validator: config["Name"]?.validator,
            value: editEntity.attributes['Name'],
            text: <HeaderText {...props} operation={operation} readOnly={readOnly} subentityTypeLabel={subentityTypeLabel} suffixRender={props.header?.suffixRender} />,
            ...props.header,
            showNameEditor: props.header?.showNameEditor !== false && !selectedTab,
            secondaryText: <ExtraTabs
                {...props}
                readOnly={readOnly}
                extraTabs={extraTabs}
                selectedTab={selectedTab}
                entityTypeLabel={subentityTypeLabel}
                isTabsDisabled={!isValid}
                onTabChange={onTabChange} />,
            fullScreen: props.header?.showFullScreenMode && subentityType ? user.preferences.expandEditPanel?.[subentityType] : undefined,
        }}
        isDirty={isDirty}
        commands={commands}
        onFullScreen={saveFullScreenPanelState}
        className={css("subentity-panel", className)}>
        <div className="panel-area">
            {extraTabs && selectedTab
                ? selectedTab.renderBody(editEntity, onChanged, setDirty)
                : <FieldsEdit
                    fields={fields}
                    entity={editEntity}
                    config={config}
                    displayFields={displayFields}
                    subentityType={subentityType}
                    onChanged={onChanged}
                />
            }
        </div>
        {children}
    </ItemCreation>;
}

export function getDeleteButtonCommand(onClick: () => void): ICommand {
    return {
        text: 'Delete',
        iconName: 'Delete',
        right: true,
        title: `Delete`,
        onClick: onClick
    };
}

export default function <TSubentity extends ISubentity>() {
    function mergeActionCreators(dispatch: any): ActionProps {
        return {
            userActions: bindActionCreators(UsersStore.actionCreators, dispatch),
        };
    }

    function mapStateToProps(state: ApplicationState, ownProps: OwnProps<TSubentity>): StateProps {
        const panelExtensions = findExtension(
            state.extensions,
            ownProps.parentEntityType ?? ownProps.subentityType,
            ownProps.operation === "Create" ? ExtensionPageLocation.EntityCreatePanel : ExtensionPageLocation.EntityEditPanel,
            ownProps.subentityType,
            ownProps.entity.sourceType ?? undefined
        );

        return {
            user: state.user,
            panelExtension: panelExtensions.length > 0 ? panelExtensions[0] : undefined,
        };
    }

    return connect(mapStateToProps, mergeActionCreators)(UpdatePanelComponent);
}